

import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useState, useRef } from "react"
import { useCreateCreditTopupQR, useCheckTransactionStatus } from "@/src/hooks/useAddCredit"
import { Card } from "@/components/ui/card"
import { useEffect } from "react"
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react"
import Image from "@/src/components/Image"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/src/contexts/AuthContext"

export default function AddCreditPage() {
  const navigate = useNavigate()
  const { refreshCredits } = useAuth()
  const hasRefreshedCredits = useRef(false)
  const [amount, setAmount] = useState("")
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [transactionId, setTransactionId] = useState<string | null>(null)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [creditsToAdd, setCreditsToAdd] = useState<number>(0)
  const [overageRate, setOverageRate] = useState<number>(1.0) // 1 THB = 1 credit by default
  const [step, setStep] = useState<'input' | 'qr' | 'confirmation'>('input')
  const [qrTimeout, setQrTimeout] = useState<number>(180) // 3 minutes in seconds

  // Debug logging for page re-renders
  console.log('🔄 AddCreditPage rendering - step:', step, 'transactionId:', transactionId)

  // Debug: Track page reload/navigation
  useEffect(() => {
    console.log('[AddCredit] Page mounted')
    const handleBeforeUnload = () => {
      console.warn('[AddCredit] Page is about to reload!')
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  const quickAmounts = [100, 300, 500, 1000, 3000, 5000, 10000]

  // Mutations and queries
  const createQRMutation = useCreateCreditTopupQR()
  const { data: statusData } = useCheckTransactionStatus(transactionId)

  const handleQuickAmount = (value: number) => {
    setAmount(value.toString())
    setSelectedAmount(value)
  }

  const handleConfirm = async () => {
    const amountNum = parseFloat(amount)
    
    if (!amount || isNaN(amountNum)) {
      toast.error('กรุณากรอกจำนวนเงิน')
      return
    }

    if (amountNum < 100) {
      toast.error('จำนวนเงินต่ำสุดคือ 100 บาท')
      return
    }

    if (amountNum > 1000000) {
      toast.error('จำนวนเงินสูงสุดคือ 1,000,000 บาท')
      return
    }

    // Create QR code
    await createQRMutation.mutateAsync(
      { amount: amountNum },
      {
        onSuccess: (data) => {
          hasRefreshedCredits.current = false
          setTransactionId(data.data.transaction.id)
          setQrCodeUrl(data.data.qrCode.qr_code)
          setCreditsToAdd(data.data.creditsToAdd)
          setOverageRate(data.data.overageRate || 1.0) // Get overage_rate from backend
          setStep('qr')
          toast.success('สร้าง QR Code สำเร็จ')
        },
        onError: (error) => {
          toast.error(`เกิดข้อผิดพลาด: ${error.message}`)
        }
      }
    )
  }

  // Monitor transaction status
  useEffect(() => {
    if (statusData?.data?.isCompleted && !hasRefreshedCredits.current) {
      hasRefreshedCredits.current = true
      setStep('confirmation')
      toast.success('ยืนยันการชำระเงินสำเร็จ! เครดิตได้รับการเติมแล้ว')
      // Refresh credits without full page reload
      refreshCredits()
    } else if (statusData?.data?.isFailed) {
      toast.error('การชำระเงินล้มเหลว กรุณาลองใหม่อีกครั้ง')
      setStep('input')
      setTransactionId(null)
      setQrCodeUrl(null)
      hasRefreshedCredits.current = false
    }
  }, [statusData?.data?.isCompleted, statusData?.data?.isFailed])

  // QR Code auto-timeout countdown
  useEffect(() => {
    if (step !== 'qr') return

    const timer = setInterval(() => {
      setQrTimeout((prev) => {
        if (prev <= 1) {
          // Auto redirect back to input
          hasRefreshedCredits.current = false
          setStep('input')
          setTransactionId(null)
          setQrCodeUrl(null)
          setAmount('')
          setSelectedAmount(null)
          toast.info('หมดเวลาแสดง QR Code แล้ว กลับไปหน้าเดิม')
          return 180
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [step])

  const isLoading = createQRMutation.isPending

  if (step === 'qr') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">เติมเครดิต</h1>

          <div className="max-w-3xl mx-auto space-y-6">
            {/* QR Code Display */}
            <Card className="p-8">
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold text-[#7FB069]">
                    สแกน QR Code เพื่อชำระเงิน
                  </h2>
                  <p className="text-gray-600">
                    จำนวนเงิน: {selectedAmount?.toLocaleString()} บาท
                  </p>
                </div>

                {qrCodeUrl && (
                  <div className="flex justify-center">
                    <img
                      src={qrCodeUrl}
                      alt="PromptPay QR Code"
                      className="w-48 h-48 border-2 border-gray-300 rounded-lg p-2 bg-white"
                    />
                  </div>
                )}

                <div className="space-y-4 p-4 bg-amber-50 rounded-lg border-2 border-amber-300">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900">⚠️ ขั้นตอนต่อไป: ส่ง Slip เข้า LINE OA</p>
                      <p className="text-sm text-amber-800 mt-1">
                        หลังจากชำระเงินแล้ว โปรดส่งสลิป (screenshot หรือภาพถ่าย) เข้า LINE OA เพื่อให้เจ้าหน้าที่ตรวจสอบ
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                  <p className="font-semibold text-blue-900">📝 วิธีการส่ง Slip:</p>
                  <ol className="space-y-2 text-sm text-blue-800 list-decimal list-inside">
                    <li>จ่ายเงินโดยการสแกน QR Code ด้านบน</li>
                    <li>ถ่ายภาพ screenshot หรือสลิปการโอนเงิน</li>
                    <li>เปิด LINE OA <span className="font-semibold\">@thaislipcheck</span></li>
                    <li>ส่งภาพ slip และระบุจำนวนเงิน {selectedAmount?.toLocaleString()} บาท</li>
                    <li>รอเจ้าหน้าที่ตรวจสอบและ Approve (โดยปกติ 1-2 ชั่วโมง)</li>
                  </ol>
                </div>

                <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-900">✓ เมื่อได้รับการ Approve</p>
                      <p className="text-sm text-green-800 mt-1">
                        เครดิตจะถูกเติมให้โดยอัตโนมัติภายใน 5-10 นาที
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="font-semibold text-amber-900">ข้อมูลการโอนเงิน:</p>
                  <ul className="space-y-2 text-sm text-amber-800">
                    <li>💳 <span className="font-semibold">ธนาคาร:</span> PromptPay</li>
                    <li>💰 <span className="font-semibold">จำนวนเงิน:</span> {selectedAmount?.toLocaleString()} บาท</li>
                    <li>🎁 <span className="font-semibold">เครดิตที่ได้รับ:</span> {creditsToAdd.toLocaleString()} เครดิต</li>
                    <li>📊 <span className="font-semibold">อัตรา:</span> 1 บาท = {(1 / overageRate).toFixed(3)} เครดิต (คำนวณจากระบบ)</li>
                  </ul>
                </div>

                {/* Countdown Timer */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center">
                  <p className="text-sm text-purple-900 mb-2">⏱️ หมดเวลาแสดง QR Code ใน</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {Math.floor(qrTimeout / 60)}:{(qrTimeout % 60).toString().padStart(2, '0')}
                  </p>
                  <p className="text-xs text-purple-800 mt-2">ระบบจะกลับไปหน้าเดิมโดยอัตโนมัติ</p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      hasRefreshedCredits.current = false
                      setStep('input')
                      setTransactionId(null)
                      setQrCodeUrl(null)
                      setAmount('')
                      setSelectedAmount(null)
                      setQrTimeout(180)
                    }}
                    className="flex-1"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    onClick={() => {
                      // Copy QR data to clipboard or download
                      toast.info('สแกน QR Code ด้านบนเพื่อชำระเงิน')
                    }}
                    className="flex-1 bg-[#7FB069] hover:bg-[#7FB069]/90"
                  >
                    เข้าใจแล้ว
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (step === 'confirmation') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">เติมเครดิต</h1>

          <div className="max-w-3xl mx-auto">
            <Card className="p-8">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-green-600">เติมเครดิตสำเร็จ!</h2>
                  <p className="text-gray-600 mt-2">
                    ระบบได้ยืนยันการชำระเงินของคุณ
                  </p>
                </div>

                <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <p className="text-sm text-gray-600">จำนวนเงินที่โอน</p>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedAmount?.toLocaleString()} บาท
                    </p>
                  </div>
                  <div className="border-t border-green-200 pt-3">
                    <p className="text-sm text-gray-600">เครดิตที่ได้รับ</p>
                    <p className="text-2xl font-bold text-green-600">
                      +{creditsToAdd.toLocaleString()} เครดิต
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-[#7FB069] hover:bg-[#7FB069]/90"
                >
                  กลับไปที่แดชบอร์ด
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">เติมเครดิต</h1>

        <div className="">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold text-center text-[#7FB069]">
              กรอกจำนวนเงินที่ต้องการเติมเข้าระบบ
            </h2>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Image
                  src="/icons/icon-money.svg"
                  alt="Money"
                  width={24}
                  height={24}
                  className="brightness-0"
                />
                <span className="font-semibold">จำนวนเงิน (บาท)</span>
              </div>

              <Input
                type="number"
                placeholder="กรอกจำนวนเงิน"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="text-lg"
                disabled={isLoading}
              />

              <div className="flex flex-wrap gap-3 justify-center">
                {quickAmounts.map((value) => (
                  <Button
                    key={value}
                    variant={selectedAmount === value ? "default" : "outline"}
                    onClick={() => handleQuickAmount(value)}
                    disabled={isLoading}
                    className={
                      selectedAmount === value
                        ? "bg-[#6FBADB] text-white min-w-[80px]"
                        : "border-[#6FBADB] text-[#6FBADB] hover:bg-[#6FBADB] hover:text-white min-w-[80px]"
                    }
                  >
                    {value.toLocaleString()}
                  </Button>
                ))}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={handleConfirm}
                  disabled={isLoading || !amount}
                  className="bg-[#E07A5F] hover:bg-[#E07A5F]/90 text-white px-8"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      กำลังสร้าง QR Code...
                    </>
                  ) : (
                    'ยืนยันจำนวนเงิน'
                  )}
                </Button>
              </div>

              <div className="text-center space-y-1 text-gray-600">
                <p>สามารถโอนเงินผ่านแอพธนาคารใดก็ได้</p>
                <p>โดยสแกน QR Code นี้เพื่อทำรายการ</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-300" />

        {/* Important Information Box */}
        <div className="">
          <div className="max-w-3xl mx-auto">
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-900 text-lg">⚠️ ระบบตรวจสอบ Slip แบบ Manual</p>
                  <p className="text-red-800 mt-2">
                    ปัจจุบันระบบเรายังใช้การตรวจสอบแบบ Manual โปรดส่ง Slip ไปยัง LINE OA <span className="font-bold\">@thaislipcheck</span> เพื่อให้เจ้าหน้าที่ตรวจสอบและ Approve
                  </p>
                  <p className="text-red-800 mt-2">
                    ⏱️ เวลาการตรวจสอบ: <span className="font-semibold\">1-2 ชั่วโมง</span> (ระหว่างเวลาทำการ)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-300" />

        <div className="">
          <div className="max-w-3xl mx-auto space-y-4">
            <h3 className="text-2xl font-bold text-[#7FB069]">วิธีเติมเครดิต</h3>

            <ol className="space-y-3 text-gray-700">
              <li>
                <span className="font-semibold">1.</span> กรอกจำนวนเงิน (บาท) ที่คุณต้องการใช้ซื้อเครดิตในช่องด้านบน
              </li>
              <li>
                <span className="font-semibold">2.</span> กรอกเป็นจำนวนเงินเต็มเท่านั้น เช่น 100 , 1,500 , 10,000
                และไม่ต้องใส่ทศนิยม
              </li>
              <li>
                <span className="font-semibold">3.</span> กดที่ปุ่ม "ยืนยันจำนวนเงิน" เพื่อรับ QR Code เพื่อใช้สำหรับชำระเงิน
              </li>
              <li>
                <span className="font-semibold">4.</span> ชำระเงินโดยการสแกน QR Code ผ่านแอพของธนาคารใดๆ ก็ได้
              </li>
              <li>
                <span className="font-semibold">5.</span> ถ่ายภาพ screenshot หรือสลิปการโอนเงิน
              </li>
              <li>
                <span className="font-semibold">6.</span> เปิด LINE OA <span className="font-semibold\">@thaislipcheck</span> และส่งภาพ slip พร้อมระบุจำนวนเงิน
              </li>
              <li>
                <span className="font-semibold">7.</span> รอเจ้าหน้าที่ตรวจสอบและ Approve (โดยปกติ 1-2 ชั่วโมง)
              </li>
              <li>
                <span className="font-semibold">8.</span> เมื่อได้รับการ Approve เครดิตจะถูกเติมให้โดยอัตโนมัติ
              </li>
            </ol>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
